import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "../i18n";
import ChapterHeading from "./ChapterHeading";

function FaqItem({ item, index, open, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="border-b border-black/10"
    >
      <button
        data-testid={`faq-item-${index}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-6 py-7 md:py-9 text-left group"
      >
        <span className="font-serif text-2xl md:text-4xl tracking-tight group-hover:text-klein transition-colors duration-300">
          {item.q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className="shrink-0 text-black/50 group-hover:text-klein transition-colors duration-300"
        >
          <Plus size={22} strokeWidth={1.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div data-testid={`faq-answer-${index}`} className="pb-8 md:pb-10 max-w-2xl">
              {item.a.split("\n\n").map((p, k) => (
                <p key={k} className="text-sm md:text-base leading-relaxed text-black/70 mb-5 last:mb-0">
                  {p}
                </p>
              ))}
            </div>
            {item.imgs && (
              <div className="pb-10 md:pb-12 grid grid-cols-2 gap-4 max-w-2xl">
                {item.imgs.map((img, j) => (
                  <div key={j} data-testid={`faq-image-${index}-${j}`} className="group overflow-hidden aspect-square">
                    <img
                      src={img}
                      alt={item.q}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function AboutIt() {
  const { t } = useLang();
  const s = t.aboutIt;
  const [open, setOpen] = useState(0);
  return (
    <section id="about-it" data-testid="about-it-section" className="py-32 md:py-48">
      <ChapterHeading num={s.num} title={s.title} note={s.note} testid="about-it-heading" />
      <div className="px-5 md:px-10 md:grid md:grid-cols-12">
        <div className="md:col-span-9 md:col-start-4 border-t border-black/10">
          {s.items.map((item, i) => (
            <FaqItem key={i} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
